import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import './FileUploadZone.css';

/**
 * Reusable drag-and-drop file upload zone.
 *
 * Provides visual feedback for drag states, click-to-upload,
 * and file type/size validation.
 *
 * @param {Object} props
 * @param {function} props.onFileSelect - Callback with selected file
 * @param {string} props.accept - Accepted file types (e.g., "image/*", "video/*")
 * @param {string} [props.prompt='Drop your file here or click to upload'] - Prompt text
 * @param {string} [props.hint] - Additional hint text below prompt
 * @param {React.ReactNode} [props.icon] - Custom icon element
 * @param {string} [props.className=''] - Additional classes
 * @returns {JSX.Element}
 */
function FileUploadZone({
  onFileSelect,
  accept,
  prompt = 'Drop your file here or click to upload',
  hint,
  icon,
  className = '',
}) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        onFileSelect(file);
      }
    },
    [onFileSelect]
  );

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div
      className={`file-upload-zone ${isDragging ? 'file-upload-zone--dragging' : ''} ${className}`.trim()}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
      aria-label={prompt}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="file-upload-zone__input"
        aria-hidden="true"
      />

      {/* Icon */}
      {icon && <div className="file-upload-zone__icon">{icon}</div>}

      {/* Prompt */}
      <p className="file-upload-zone__prompt">{prompt}</p>

      {/* Hint */}
      {hint && <p className="file-upload-zone__hint">{hint}</p>}

      {/* Drag overlay */}
      {isDragging && (
        <div className="file-upload-zone__overlay">
          <span>Drop to upload</span>
        </div>
      )}
    </div>
  );
}

FileUploadZone.propTypes = {
  onFileSelect: PropTypes.func.isRequired,
  accept: PropTypes.string.isRequired,
  prompt: PropTypes.string,
  hint: PropTypes.string,
  icon: PropTypes.node,
  className: PropTypes.string,
};

export default FileUploadZone;
